const isFinancialRow = (row) =>
  row &&
  (row.is_grouped ||
    row.status === 'completed' ||
    (row.status !== 'refunded' && row.status !== 'failed' && parseFloat(row.amount || 0) > 0));

const isPrivateBookingRow = (row) =>
  row?.is_private_booking === true ||
  row?.booking_source === 'vendor_manual' ||
  row?.booking?.booking_source === 'vendor_manual';

/** @param {{ amount?: number|string, platform_fee?: number|string, status?: string, is_grouped?: boolean }} row */
export const getVendorPayout = (row) => {
  if (!isFinancialRow(row) || isPrivateBookingRow(row)) return 0;
  const amount = parseFloat(row.amount || 0);
  const fee = parseFloat(row.platform_fee || 0);
  return Math.max(0, amount - fee);
};

export const getPlatformCommission = (row) => {
  if (!isFinancialRow(row) || isPrivateBookingRow(row)) return 0;
  return Math.max(0, parseFloat(row.platform_fee || 0));
};

export const formatPaymentTypeLabel = (type) =>
  String(type || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const resolveGroupStatus = (items) => {
  if (items.some((p) => p.status === 'refunded')) return 'refunded';
  if (items.some((p) => p.status === 'failed')) return 'failed';
  if (items.every((p) => p.status === 'completed')) return 'completed';
  return items[0]?.status || 'completed';
};

/**
 * Combine multiple payments for the same booking into one table row.
 */
export const groupPaymentsByBooking = (payments) => {
  const groups = new Map();

  for (const payment of payments || []) {
    const key = payment.booking_id ? String(payment.booking_id) : `orphan-${payment.id}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(payment);
  }

  return Array.from(groups.entries())
    .map(([, items]) => {
      const sorted = [...items].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      const latest = sorted[0];
      const booking = sorted.find((p) => p.booking)?.booking || latest.booking;
      const booking_id = latest.booking_id;

      const completedItems = items.filter((p) => p.status === 'completed');
      const paidTotal = completedItems.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
      const platformFee = completedItems.reduce((s, p) => s + parseFloat(p.platform_fee || 0), 0);

      const types = [
        ...new Set(items.map((p) => p.payment_type || p.type).filter(Boolean)),
      ];
      let payment_type = null;
      if (types.length === 1) {
        payment_type = formatPaymentTypeLabel(types[0]);
      } else if (types.length > 1) {
        payment_type = types.map(formatPaymentTypeLabel).join(' · ');
      } else if (items.length > 1) {
        payment_type = `${items.length} payments`;
      }

      const status = resolveGroupStatus(items);
      const isRefund = status === 'refunded';
      const isPrivate =
        items.some((p) => p.is_private_booking) || booking?.booking_source === 'vendor_manual';

      return {
        ...latest,
        id: `booking-${booking_id || latest.id}`,
        booking_id,
        booking,
        booking_source: booking?.booking_source,
        is_private_booking: isPrivate,
        booking_reference: booking?.booking_reference,
        amount: isRefund ? 0 : paidTotal,
        platform_fee: isRefund ? 0 : platformFee,
        payment_type,
        payment_count: items.length,
        is_grouped: items.length > 1,
        created_at: latest.created_at,
        status,
        type: latest.type || (isRefund ? 'refund' : 'payment'),
        payment_items: [...items].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
        ),
      };
    })
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
};

export const formatAdminMoney = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

export const sumVendorPayout = (rows) =>
  (rows || []).reduce((sum, row) => sum + getVendorPayout(row), 0);

export const sumCommission = (rows) =>
  (rows || []).reduce((sum, row) => sum + getPlatformCommission(row), 0);
